import {omit} from 'lodash';
import React, {FC, useCallback, useMemo, useState} from 'react';

import {LegacyApplicationModel} from '../../models/api/legacyApplicationModel';
import {LegacyApplicationEngine, SendContext, SendContextRegistrator} from './legacyApplicationEngine';
import {LegacyApplicationsContextProps, LegacyApplicationsContextProvider} from './legacyApplicationsContext';

/*
 * Props.
 */

interface LegacyApplicationsProviderProps {
  applications: ReadonlyArray<LegacyApplicationModel>;
}

/*
 * Component.
 */

export const LegacyApplicationsProvider: FC<LegacyApplicationsProviderProps> = ({applications, children}) => {
  const [sendContextMap, setSendContextMap] = useState<{[T: number]: SendContext}>({});

  /*
   * Context.
   */

  const registerSendContext: SendContextRegistrator = useCallback((applicationId, sendContext) => {
    // Add the new entry points.
    setSendContextMap((value) => ({...value, [applicationId]: sendContext}));

    // Return a function to remove them.
    return () => setSendContextMap((value) => omit(value, applicationId));
  }, []);

  const context = useMemo<LegacyApplicationsContextProps>(
    () => ({
      findSendContext: (applicationId) => sendContextMap[applicationId],
    }),
    [sendContextMap],
  );

  /*
   * Render.
   */

  return (
    <>
      {applications.map((a) => (
        <LegacyApplicationEngine key={a.id} application={a} registerSendContext={registerSendContext} />
      ))}
      <LegacyApplicationsContextProvider value={context}>{children}</LegacyApplicationsContextProvider>
    </>
  );
};
